import { forwardRef, useImperativeHandle, useEffect, useState, useRef } from 'react'
import { StepCardContent } from './StepCard'
import { useStepNavStore } from '../../stores/useStepNavStore'
import {
  Input,
  Button,
  Textarea,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@bpinternal/ui-kit-next'
import { ChevronDown } from 'lucide-react'
import { getUseBotOnboardingStore, BusinessSource } from '../../stores/useBotOnboardingStore'
import type { StepComponentHandle } from './CreateBotOnboarding'
import { getApiClient, useBotCreationStore, type Status } from '~/stores'
import { safeParseURLS } from '../../utils/urls'
import { promiseWithTimeout } from '~/utils'

const MAX_CHARACTERS = 500

type Props = {
  workspaceId: string
  botId: string
}

export const BusinessStep = forwardRef<StepComponentHandle, Props>(({ workspaceId, botId }, ref) => {
  const useBotOnboardingStore = getUseBotOnboardingStore({ botId, workspaceId })
  const business = useBotOnboardingStore((state) => state.business)
  const setBusiness = useBotOnboardingStore((state) => state.setBusiness)
  const setKnowledgeBaseInputUrl = useBotOnboardingStore((state) => state.setKnowledgeBaseInputUrl)
  const startIdentityWorkflow = useBotOnboardingStore((state) => state.startIdentityWorkflow)
  const setProgressBarStep = useStepNavStore((state) => state.setProgressBarStep)
  const setWebsiteUrlsTree = useBotOnboardingStore((state) => state.setWebsiteUrlsTree)

  const [businessSource, setBusinessSource] = useState<BusinessSource>(
    business.richText.length === 0 ? 'website' : 'richText'
  )
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const nextLoading = useStepNavStore((state) => state.nextLoading)
  const setNextLoading = useStepNavStore((state) => state.setNextLoading)

  const client = getApiClient({ workspaceId, botId })
  const inputRef = useRef<HTMLInputElement>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setProgressBarStep(3)
    handleFocus()
  }, [])

  const handleFocus = () => {
    if (businessSource === 'website') {
      inputRef.current?.focus()
    } else {
      textAreaRef.current?.focus()
    }
  }

  const handleRichTextInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    if (value.length <= MAX_CHARACTERS) {
      setBusiness({ websiteUrl: '', richText: value })
    }
  }

  useImperativeHandle(ref, () => ({
    handleNext: async () => {
      setNextLoading(true)

      if (business.websiteUrl.length > 0) {
        const discoveredURLsPromise = promiseWithTimeout(discoverURLs(), 30000).catch(() => [])
        const browsePagesPromise = browsePageFallback().catch(() => undefined)

        const [discoveredURLsResult, browsePagesResult] = await Promise.allSettled([
          discoveredURLsPromise,
          browsePagesPromise,
        ])

        const discoveredURLs = discoveredURLsResult.status === 'fulfilled' ? discoveredURLsResult.value : []
        const browsePageContent = browsePagesResult.status === 'fulfilled' ? browsePagesResult.value : undefined
        ;(await awaitBotCreationState('deployed'))()

        if (discoveredURLs?.length === 0 && !browsePageContent) {
          setNextLoading(false)
          return Promise.reject(new Error('Something went wrong. Please verify the website URL and try again.'))
        }

        if (discoveredURLs?.length > 0) {
          setKnowledgeBaseInputUrl(business.websiteUrl)
          startIdentityWorkflow(discoveredURLs)
        } else {
          startIdentityWorkflow([], browsePageContent?.description)
        }
      } else {
        ;(await awaitBotCreationState('deployed'))()
        startIdentityWorkflow([])
      }

      setNextLoading(false)
    },
  }))

  const discoverURLs = async () => {
    let parsedURLs: string[] = []
    const url = business.websiteUrl.includes('://') ? business.websiteUrl : `https://${business.websiteUrl}`
    const discoveredURLs = await client.callAction({
      type: 'browser:discoverUrls',
      input: {
        url,
        onlyHttps: true,
        count: 100,
      },
    })

    parsedURLs = safeParseURLS(discoveredURLs.output?.urls ?? [])

    setWebsiteUrlsTree({
      urls: parsedURLs,
      checked: [],
      expanded: [],
    })
    return parsedURLs
  }

  const browsePageFallback = async () => {
    const url = business.websiteUrl.includes('://') ? business.websiteUrl : `https://${business.websiteUrl}`
    const pagesContent = await client.callAction({
      type: 'browser:browsePages',
      input: {
        urls: [url],
        timeout: 30000,
      },
    })
    return pagesContent.output?.results.at(0) as { description: string } | undefined
  }

  const awaitBotCreationState = async (desiredStatus: Status) => {
    return new Promise<() => void>((resolve) => {
      if (useBotCreationStore.getState().botsBeingCreated[botId] === desiredStatus) {
        return resolve(() => {})
      }
      const unsubscribe = useBotCreationStore.subscribe((state) => {
        const status = state.botsBeingCreated[botId]
        if (status === desiredStatus) {
          resolve(unsubscribe)
        }
      })
    })
  }

  return (
    <StepCardContent
      className="flex flex-1"
      title="Personality"
      description="Share a website or description to shape your bot’s tone and appearance."
    >
      <div className="flex flex-1 flex-col justify-end gap-4 lg:justify-center">
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <div className="flex items-center justify-end gap-4 text-sm text-muted-foreground">
            <p className="text-base font-medium text-gray-11">Description from</p>
            <DropdownMenuTrigger asChild className="self-end">
              <Button variant="secondary" onClick={() => setDropdownOpen(true)} className="flex justify-between">
                <p className="w-14 text-left">{businessSource === 'website' ? 'Website' : 'Text'}</p>
                <ChevronDown className="" />
              </Button>
            </DropdownMenuTrigger>
          </div>
          <DropdownMenuContent className="flex flex-col" align="start">
            <Button
              variant="ghost"
              className="justify-start text-left"
              onClick={() => {
                setBusinessSource('website')
                setDropdownOpen(false)
              }}
            >
              Website
            </Button>
            <Button
              variant="ghost"
              className="justify-start text-left"
              onClick={() => {
                setBusinessSource('richText')
                setDropdownOpen(false)
              }}
            >
              Text
            </Button>
          </DropdownMenuContent>
        </DropdownMenu>

        {businessSource === 'website' ? (
          <Input
            ref={inputRef}
            className="h-10"
            placeholder="https://yourwebsite.com"
            value={business.websiteUrl}
            disabled={nextLoading}
            onChange={(e) => setBusiness({ websiteUrl: e.target.value, richText: '' })}
          />
        ) : (
          <div className="flex flex-1 flex-col gap-2">
            <Textarea
              ref={textAreaRef}
              className="flex-1 resize-none"
              value={business.richText}
              disabled={nextLoading}
              onChange={handleRichTextInput}
              placeholder={`Share FAQs, guides, or any info your agent can use to help`}
            />
            <p className="text-end text-sm text-muted-foreground">
              {MAX_CHARACTERS - business.richText.length} characters remaining
            </p>
          </div>
        )}
      </div>
    </StepCardContent>
  )
})
BusinessStep.displayName = 'BusinessStep'
